import { create } from 'zustand';
import { api } from '../services/apiService';
import type { Task, TaskStats, TaskTemplate, PreInstructions, TaskExecutionLog } from '../types/tasks';

interface TaskFilters {
  status?: string;
  agent?: string;
  department?: string;
  priority?: string;
  cadence?: string;
}

interface TaskState {
  // Tasks
  tasks: Task[];
  stats: TaskStats | null;
  loading: boolean;
  filters: TaskFilters;
  fetchTasks: () => Promise<void>;
  fetchStats: () => Promise<void>;
  createTask: (data: Partial<Task>) => Promise<Task>;
  updateTask: (id: number, data: Partial<Task>) => Promise<Task>;
  deleteTask: (id: number) => Promise<void>;
  setFilters: (filters: Partial<TaskFilters>) => void;

  // Templates
  templates: TaskTemplate[];
  templatesLoading: boolean;
  fetchTemplates: () => Promise<void>;
  createTemplate: (data: Partial<TaskTemplate>) => Promise<TaskTemplate>;
  updateTemplate: (id: string, data: Partial<TaskTemplate>) => Promise<TaskTemplate>;
  deleteTemplate: (id: string) => Promise<void>;
  instantiateTemplate: (id: string) => Promise<Task>;

  // Pre-instructions
  preInstructions: PreInstructions | null;
  preInstructionsLoading: boolean;
  fetchPreInstructions: () => Promise<void>;
  updatePreInstructions: (content: string) => Promise<void>;

  // Execution logs
  fetchExecutionLogs: (taskId: number) => Promise<TaskExecutionLog[]>;
}

function buildQueryString(filters: TaskFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.agent) params.set('assigned_agent', filters.agent);
  if (filters.department) params.set('department', filters.department);
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.cadence) params.set('cadence', filters.cadence);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const useTaskStore = create<TaskState>((set, get) => ({
  // ── Tasks ──────────────────────────────────────────────────────────────
  tasks: [],
  stats: null,
  loading: false,
  filters: { cadence: 'weekly+' },

  fetchTasks: async () => {
    try {
      set({ loading: true });
      const qs = buildQueryString(get().filters);
      const tasks = await api.get<Task[]>(`/tasks${qs}`);
      set({ tasks, loading: false });
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
      set({ loading: false });
    }
  },

  fetchStats: async () => {
    try {
      const stats = await api.get<TaskStats>('/tasks/stats');
      set({ stats });
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  },

  createTask: async (data: Partial<Task>) => {
    const task = await api.post<Task>('/tasks', data);
    await get().fetchTasks();
    await get().fetchStats();
    return task;
  },

  updateTask: async (id: number, data: Partial<Task>) => {
    const task = await api.patch<Task>(`/tasks/${id}`, data);
    await get().fetchTasks();
    await get().fetchStats();
    return task;
  },

  deleteTask: async (id: number) => {
    await api.delete(`/tasks/${id}`);
    await get().fetchTasks();
    await get().fetchStats();
  },

  setFilters: (newFilters: Partial<TaskFilters>) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    }));
    get().fetchTasks();
  },

  // ── Templates ──────────────────────────────────────────────────────────
  templates: [],
  templatesLoading: false,

  fetchTemplates: async () => {
    try {
      set({ templatesLoading: true });
      const templates = await api.get<TaskTemplate[]>('/tasks/templates');
      set({ templates, templatesLoading: false });
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      set({ templatesLoading: false });
    }
  },

  createTemplate: async (data: Partial<TaskTemplate>) => {
    const template = await api.post<TaskTemplate>('/tasks/templates', data);
    await get().fetchTemplates();
    return template;
  },

  updateTemplate: async (id: string, data: Partial<TaskTemplate>) => {
    const template = await api.patch<TaskTemplate>(`/tasks/templates/${id}`, data);
    await get().fetchTemplates();
    return template;
  },

  deleteTemplate: async (id: string) => {
    await api.delete(`/tasks/templates/${id}`);
    await get().fetchTemplates();
  },

  instantiateTemplate: async (id: string) => {
    const task = await api.post<Task>(`/tasks/templates/${id}/instantiate`, {});
    await get().fetchTasks();
    await get().fetchStats();
    return task;
  },

  // ── Pre-instructions ──────────────────────────────────────────────────
  preInstructions: null,
  preInstructionsLoading: false,

  fetchPreInstructions: async () => {
    try {
      set({ preInstructionsLoading: true });
      const data = await api.get<PreInstructions>('/tasks/pre-instructions');
      set({ preInstructions: data, preInstructionsLoading: false });
    } catch (err) {
      console.error('Failed to fetch pre-instructions:', err);
      set({ preInstructionsLoading: false });
    }
  },

  updatePreInstructions: async (content: string) => {
    const data = await api.patch<PreInstructions>('/tasks/pre-instructions', { content });
    set({ preInstructions: data });
  },

  // ── Execution Logs ────────────────────────────────────────────────────
  fetchExecutionLogs: async (taskId: number) => {
    try {
      return await api.get<TaskExecutionLog[]>(`/tasks/${taskId}/logs`);
    } catch (err) {
      console.error('Failed to fetch execution logs:', err);
      return [];
    }
  },
}));
